import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { ApiService } from '../utils/api';
import { formatDate } from '../utils/datetime';
import { Calendar, ExternalLink, MapPin, Users } from 'lucide-react';

const dateRange = (event) => (event.eventEndDate && formatDate(event.eventEndDate) !== formatDate(event.eventDate)
  ? `${formatDate(event.eventDate)} – ${formatDate(event.eventEndDate)}`
  : formatDate(event.eventDate));

const EventCard = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const longAbout = (event.about || '').length > 240;
  const links = [
    event.officialUrl && { label: 'Official website', url: event.officialUrl },
    event.organiserUrl && { label: 'Organising committee', url: event.organiserUrl },
    ...(event.links || []),
  ].filter(Boolean);

  return (
    <article className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        {event.logoUrl && <img src={event.logoUrl} alt="" className="h-14 w-14 shrink-0 rounded object-contain bg-white" />}
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">{event.name}</h2>
          <div className="mt-1 space-y-0.5 text-sm text-gray-600">
            {event.eventDate && <div className="flex items-center"><Calendar className="mr-2 h-4 w-4 shrink-0" />{dateRange(event)}</div>}
            {event.location && <div className="flex items-center"><MapPin className="mr-2 h-4 w-4 shrink-0" /><span>{event.location}</span></div>}
          </div>
        </div>
      </div>
      {event.about && (
        <p className="mt-3 whitespace-pre-line text-sm text-gray-700">
          {expanded || !longAbout ? event.about : `${event.about.slice(0, 240)}…`}
          {longAbout && (
            <button type="button" className="ml-1 text-blue-600 hover:underline" onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Less' : 'More'}
            </button>
          )}
        </p>
      )}
      {links.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {links.map((link) => (
            <a key={`${link.label}-${link.url}`} href={link.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:underline">
              {link.label} <ExternalLink className="ml-1 h-3 w-3" />
            </a>
          ))}
        </div>
      )}
      <div className="mt-auto flex items-center justify-between gap-3 pt-4">
        <span className="flex items-center text-sm text-gray-600">
          <Users className="mr-1 h-4 w-4" />{event.registrations.toLocaleString('en-IN')} registered
          <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs">{event.paid ? 'Paid' : 'Free'}</span>
        </span>
        {event.registrationOpen ? (
          <Link to={`/register/${event.slug || event.id}`}><Button size="sm">Register</Button></Link>
        ) : (
          <span className="text-xs text-gray-500">Registration not open here</span>
        )}
      </div>
    </article>
  );
};

const PublicEventsPage = () => {
  const { data: events = [], isLoading, error } = useQuery({
    queryKey: ['publicWebsiteEvents'],
    queryFn: () => ApiService.getPublicWebsiteEvents(),
    staleTime: 60 * 1000,
  });
  const { data: content } = useQuery({
    queryKey: ['websiteContent'],
    queryFn: () => ApiService.getWebsiteContent(),
    staleTime: 60 * 1000,
    retry: false,
  });
  const logos = content?.logos || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <header className="bg-white shadow-sm">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/"><img src="/logo.png" alt="Home" className="h-14 w-auto" /></Link>
          <Link to="/demo"><Button variant="outline">Book a demo</Button></Link>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900">Upcoming events</h1>
        <p className="mt-1 text-gray-600">Events currently running on our platform.</p>

        {logos.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 rounded-xl bg-white/70 p-4">
            {logos.map((logo) => {
              const img = <img src={ApiService.galleryPhotoUrl(logo.id)} alt={logo.caption || ''} title={logo.caption || ''} className="h-12 w-auto max-w-[160px] object-contain" loading="lazy" />;
              return logo.link
                ? <a key={logo.id} href={logo.link} target="_blank" rel="noopener noreferrer">{img}</a>
                : <span key={logo.id}>{img}</span>;
            })}
          </div>
        )}

        {isLoading && <p className="mt-10 text-center text-gray-500">Loading events…</p>}
        {error && <p className="mt-10 text-center text-red-600">Could not load events. Please try again later.</p>}
        {!isLoading && !error && events.length === 0 && (
          <p className="mt-10 text-center text-gray-600">No upcoming events right now. Check back soon.</p>
        )}
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {events.map((event) => <EventCard key={event.id} event={event} />)}
        </div>
      </main>
    </div>
  );
};

export default PublicEventsPage;
